import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Push Token Bridge for AppBuild.diy wrapper
 * 
 * Enriches AppBuild push tokens with authenticated Mateo user data.
 * Runs on every authenticated page load inside the wrapper to ensure
 * devices are always linked, even if previous enrichment attempts failed.
 * 
 * Uses sessionStorage to avoid spamming the API on every navigation,
 * but re-enriches on each fresh app launch or sign-in.
 */
export const useAppBuildTokenBridge = () => {
  const enrichedRef = useRef(false);

  useEffect(() => {
    // Detect if we're inside any kind of iOS webview wrapper
    const ua = navigator.userAgent;
    const isIOSWebView =
      // AppBuild specific
      /AppBuild/i.test(ua) ||
      (window as any).AppBuild !== undefined ||
      // Generic iOS WebView detection (WKWebView doesn't include "Safari")
      (/iPhone|iPad|iPod/.test(ua) && !/Safari/.test(ua)) ||
      // webkit messageHandlers present (native bridge)
      (window as any).webkit?.messageHandlers !== undefined;

    // Also run for standalone PWA mode on iOS
    const isStandalonePWA = (window.navigator as any).standalone === true;

    const shouldRun = isIOSWebView || isStandalonePWA;

    if (!shouldRun) return;

    console.log('[TokenBridge] Detected iOS wrapper/PWA environment');

    const ENRICHMENT_SESSION_KEY = 'mateo_token_enriched';

    const enrichToken = async (pushToken?: string, force = false) => {
      // Skip if already enriched this session (unless forced)
      if (!force && (enrichedRef.current || sessionStorage.getItem(ENRICHMENT_SESSION_KEY))) {
        console.log('[TokenBridge] Already enriched this session, skipping');
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('[TokenBridge] No authenticated user, skipping enrichment');
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('email, full_name')
          .eq('id', user.id)
          .single();

        const payload: Record<string, any> = {
          externalUserId: user.id,
          userEmail: profile?.email || user.email,
          customAttributes: {
            fullName: profile?.full_name || '',
            platform: 'mateo',
          },
        };

        if (pushToken) {
          payload.pushToken = pushToken;
        }

        console.log('[TokenBridge] Enriching push token for user:', user.id, 'email:', payload.userEmail);

        const { data, error } = await supabase.functions.invoke('enrich-push-token', {
          body: payload,
        });

        if (error) {
          console.error('[TokenBridge] Enrichment failed:', error);
        } else {
          console.log('[TokenBridge] Token enriched successfully:', data);
          enrichedRef.current = true;
          sessionStorage.setItem(ENRICHMENT_SESSION_KEY, Date.now().toString());
        }
      } catch (err) {
        console.error('[TokenBridge] Error:', err);
      }
    };

    // Listen for native app ready event
    const handleMobileAppReady = (event: Event) => {
      console.log('[TokenBridge] mobileAppReady received');
      const customEvent = event as CustomEvent;
      const pushToken = customEvent?.detail?.pushToken;
      enrichToken(pushToken, true);
    };

    // Listen for postMessage events
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'pushTokenReady' || event.data?.type === 'mobileAppReady') {
        console.log('[TokenBridge] Message event received:', event.data.type);
        enrichToken(event.data.pushToken, true);
      }
    };

    // Re-enrich on sign-in
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        console.log('[TokenBridge] Auth signed in, enriching token');
        // Clear session flag so we re-enrich
        sessionStorage.removeItem(ENRICHMENT_SESSION_KEY);
        enrichedRef.current = false;
        setTimeout(() => enrichToken(undefined, true), 1500);
      }
    });

    window.addEventListener('mobileAppReady', handleMobileAppReady);
    window.addEventListener('message', handleMessage);

    // Check if native app already set token before React mounted (race condition fix)
    const preloadedToken =
      (window as any).__mateo_push_token ||
      (window as any).AppBuild?.pushToken ||
      (window as any).__appbuild_push_token;

    if (preloadedToken) {
      console.log('[TokenBridge] Found pre-loaded native push token');
      enrichToken(preloadedToken, true);
    } else {
      // Run enrichment on mount (links user even without token)
      enrichToken();
    }

    // Retry after a delay in case native token arrives late
    const retryTimer = setTimeout(() => {
      const lateToken =
        (window as any).__mateo_push_token ||
        (window as any).AppBuild?.pushToken ||
        (window as any).__appbuild_push_token;
      if (lateToken && !enrichedRef.current) {
        console.log('[TokenBridge] Late native token detected, enriching');
        enrichToken(lateToken, true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('mobileAppReady', handleMobileAppReady);
      window.removeEventListener('message', handleMessage);
      clearTimeout(retryTimer);
      subscription.unsubscribe();
    };
  }, []);
};
